/** The itinerary record's cards (plan §5 "Cards", §2 "Cards"): a `ViewLayout` over the
 *  itinerary registry (`itinerarySections`) — Catalog entry, App button, Days (full width),
 *  Content, Visibility & search, Access, Identity & source — in the order the app presents
 *  the itinerary.
 *
 *  The records' width rule: the layout follows the width the cards have in EDIT mode, in both
 *  modes. From 1100 px of content the dock (`PREVIEW_DOCK_WIDTH`, 380 px) sits beside the cards
 *  and takes its column out of that width; two card columns only from 1200 px of card width,
 *  else one in page order. Each card's field grid is pinned to its edit-mode column count
 *  (232 px minimum, 16 px gutters), so entering edit mode re-lays nothing out.
 *
 *  The rows (`itineraryCardRows`) are the loading skeleton's too, so it stands where the cards
 *  land.
 *
 *  The App button card opens on its verdict, a full row (`CtaVerdictLine`). The Days card is
 *  its own component (`DaysCard`: the Day ribbon over the days' outline), across both columns. */
import { Box } from '@mui/material';
import { useMemo, type ReactNode } from 'react';

import type { ItineraryDetail } from '@/api/itineraries';
import {
  ITINERARY_RECORD_SECTIONS,
  type ItineraryRecordFieldKey,
  type ItineraryRecordSection,
} from '@/api/itineraryRecord';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid, type FieldCell } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { LEDGER_GAP_PX, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import type { Baselines } from '@/features/records/outline/tree';
import { cardSkeletonShape, RecordCardSkeleton } from '@/features/records/page/RecordStates';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import {
  CARD_GAP_PX,
  RecordCardRows,
  type RecordCardColumns,
  type RecordCardRow,
} from '@/features/records/panels/RecordCardRows';
import { RecordPanel } from '@/features/records/panels/RecordPanel';
import { PREVIEW_DOCK_WIDTH } from '@/features/records/preview/PreviewDock';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { DaysCard, DaysCardSkeleton } from '../days/DaysCard';
import type { DaysChannel } from '../days/daysChannel';
import { itineraryRec } from '../viewConfig';

import { CtaVerdictLine, itineraryFieldActions, itineraryFieldDisplay, type ItineraryDisplayContext } from './displays';
import { FROM_BRANCH, itinerarySectionAnchorId, itinerarySections } from './layout';

/** Two card columns from this much card width (the tour and activity records' rule). */
export const ITINERARY_TWO_COLUMNS_FROM_PX = 1200;

/** @param width the page content's width (dock included); 0 = unmeasured. */
export function itineraryColumns(width: number): RecordCardColumns {
  if (width === 0) return { docked: true, editWidth: 0, columns: 1 };
  const docked = width >= SHEET_BELOW_PX;
  const editWidth = docked ? width - PREVIEW_DOCK_WIDTH - LEDGER_GAP_PX : width;
  return { docked, editWidth, columns: editWidth >= ITINERARY_TWO_COLUMNS_FROM_PX ? 2 : 1 };
}

/** Values that take the whole row: long text, the rich texts, the access tags and the twelve
 *  month toggles (one row in both modes). */
const WIDE: ReadonlySet<ItineraryRecordFieldKey> = new Set([
  'description',
  'highlights',
  'notes',
  'packing_tips',
  'footer',
  'months',
  'atag',
]);

const isWide = (key: string) => WIDE.has(key as ItineraryRecordFieldKey);

/** The cards' sections, whatever the itinerary: the loading skeleton draws them before the
 *  detail arrives. */
const SKELETON_SECTIONS = new Map(itinerarySections({ id: 0 }).map((s) => [s.id, s]));

/** A card while the itinerary loads, slot for slot (`cardSkeletonShape`); the Days card's rows
 *  are `dayRows` (null: unknown). */
export function itineraryCardSkeleton(id: ItineraryRecordSection, dayRows: number | null): ReactNode {
  if (id === 'days') return <DaysCardSkeleton rows={dayRows} />;
  const section = SKELETON_SECTIONS.get(id);
  return section ? <RecordCardSkeleton blocks={cardSkeletonShape(section, isWide)} /> : null;
}

/** Two columns: Catalog entry beside App button, Days across both, then Content and Access
 *  on the left, Visibility & search and Identity & source on the right. */
const TOP_LEFT: ItineraryRecordSection[] = ['catalog_entry'];
const TOP_RIGHT: ItineraryRecordSection[] = ['app_button'];
const LEFT: ItineraryRecordSection[] = ['content', 'access'];
const RIGHT: ItineraryRecordSection[] = ['visibility', 'identity'];

/** The cards' rows for a column count: one column in page order; two as above. */
export function itineraryCardRows(columns: 1 | 2): RecordCardRow<ItineraryRecordSection>[] {
  return columns === 2
    ? [{ left: TOP_LEFT, right: TOP_RIGHT }, { full: ['days'] }, { left: LEFT, right: RIGHT }]
    : [{ full: ITINERARY_RECORD_SECTIONS }];
}

function ItineraryFieldGrid({
  fields,
  display,
  lead,
}: {
  fields: ViewFieldRef[];
  display: ItineraryDisplayContext;
  /** A full row before the fields (the App button's verdict). */
  lead?: ReactNode;
}) {
  const { fieldOf } = useRecordEditor();
  const rec = itineraryRec(display.detail.id);
  const cells: FieldCell[] = fields.map((f) => {
    const key = f.key as ItineraryRecordFieldKey;
    const shown = itineraryFieldDisplay(key, display);
    const actions = itineraryFieldActions(key);
    return {
      key: `${f.entity}:${f.id}:${f.key}`,
      rec: `${f.entity}:${f.id}`,
      path: f.path,
      wide: isWide(key) && fieldOf(f.entity, key) !== undefined,
      node: (
        <FieldSlot
          fieldKey={f.key}
          entity={f.entity}
          id={f.id}
          path={f.path}
          inlineChoices={false}
          {...(shown !== undefined ? { display: shown } : {})}
          {...(actions ? { actions } : {})}
        />
      ),
    };
  });
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={lead ? [{ key: `${rec}:lead`, rec, path: 'lead', wide: true, node: lead }, ...cells] : cells}
    />
  );
}

function ItinerarySectionPanel({
  section,
  display,
}: {
  section: ViewSection<ItineraryRecordSection>;
  display: ItineraryDisplayContext;
}) {
  const lead = section.id === 'app_button' ? <CtaVerdictLine detail={display.detail} /> : undefined;
  const editable = section.blocks.find((b) => b.kind === 'editable')?.fields;
  return (
    <RecordPanel
      section={section}
      anchorId={itinerarySectionAnchorId(section.id)}
      vendorNote={FROM_BRANCH.note}
      renderFields={(g) => (
        // The verdict leads the editable block (the panel hands it over as one group).
        <ItineraryFieldGrid fields={g.fields} display={display} {...(lead && g.fields === editable ? { lead } : {})} />
      )}
    />
  );
}

export interface ItineraryCardsViewProps {
  detail: ItineraryDetail;
  /** The draft's stored values (the Days card's outline reads its days from them). */
  baselines: Baselines;
  display: ItineraryDisplayContext;
  /** The page content's width (dock included); 0 = unmeasured. */
  width: number;
  /** The operator may edit the itinerary (edit mode makes the Days card's outline live). */
  canEdit?: boolean;
  /** What the page tells the Days card. */
  channel?: DaysChannel;
}

export function ItineraryCardsView({ detail, baselines, display, width, canEdit = false, channel }: ItineraryCardsViewProps) {
  const { editWidth, columns } = itineraryColumns(width);
  const sections = useMemo(() => itinerarySections(detail), [detail]);
  const by = new Map(sections.map((s) => [s.id, s]));

  const card = (id: ItineraryRecordSection): ReactNode => {
    const section = by.get(id);
    if (!section) return null;
    if (id === 'days') {
      return (
        <DaysCard
          key={id}
          detail={detail}
          baselines={baselines}
          anchorId={itinerarySectionAnchorId(id)}
          title={section.title}
          canEdit={canEdit}
          {...(channel ? { channel } : {})}
        />
      );
    }
    return <ItinerarySectionPanel key={id} section={section} display={display} />;
  };

  return (
    <Box data-itinerary-cards data-columns={columns} sx={{ display: 'grid', gap: `${CARD_GAP_PX}px`, minWidth: 0 }}>
      <RecordCardRows rows={itineraryCardRows(columns)} editWidth={editWidth} card={card} />
    </Box>
  );
}
